import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { formatDateTime } from "../../utils/date";
import { getHardCopyMemberNames, openReport } from "../../utils/orderReports";

const formatCurrency = (value) =>
  value === undefined || value === null || value === "" ? "-" : `₹ ${value}`;
const formatOrderId = (order) => {
  const source = order?.orderDisplayId || order?.orderId || order?.orderNumber;
  if (source && String(source).length <= 4)
    return String(source).padStart(4, "0");
  return order?.orderNumber || order?._id || "-";
};
const getLabStatus = (order) =>
  order?.labStatus || order?.deliveryStatus || "Pending";
const getAddressValue = (address, keys) => {
  for (const key of keys) {
    if (
      address?.[key] !== undefined &&
      address?.[key] !== null &&
      address?.[key] !== ""
    ) {
      return address[key];
    }
  }
  return "-";
};
const formatItemType = (type) => {
  const normalized = String(type || "").trim().toLowerCase();
  if (normalized === "test") return "Test";
  if (normalized === "package") return "Package";
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : "-";
};
const getSlotPeriod = (slot) =>
  slot?.slotPeriod ||
  slot?.period ||
  [slot?.startTime, slot?.endTime].filter(Boolean).join(" - ") ||
  "-";
const formatAmount = (value) =>
  `₹ ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const getHardCopyLabel = (order) => {
  if (!order?.hardCopy) return "No";
  const fee = formatAmount(order.priceSummary?.hardCopyFee ?? order.hardCopyFee);
  return `${getHardCopyMemberNames(order).join(", ")} (${fee})`;
};
const HardCopyBadge = ({ member }) =>
  member?.hardCopy ? <span className="badge bg-success ms-2 fw-normal">Hard copy</span> : null;
const isPackageItem = (item) => String(item?.type || "").toLowerCase() === "package";
const getItemParameters = (item) => {
  const values = item.parameters || item.tests || item.testDetails || [];
  if (Array.isArray(values) && values.length) {
    return values
      .map((entry) => entry?.name || entry?.title || entry)
      .join(", ");
  }
  return item.totalParameters ?? "-";
};

const ViewOrder = () => {
  const { id, userId, orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const isUserScopedOrder = Boolean(userId && orderId);
        const endpoint = isUserScopedOrder
          ? `/admin/user/${userId}/orders/${orderId}`
          : `/admin/orders/${id}`;
        const res = await AuthInterceptor(endpoint, {
          method: "GET",
        });
        setOrder(res?.data?.s === 200 && res.data?.r ? res.data.r : null);
      } catch (error) {
        setSnack({
          notification: {
            message: "Failed to fetch order details",
            type: "error",
          },
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id, userId, orderId]);

  const cancelOrder = async () => {
    try {
      setCancelling(true);
      const targetOrderId = order?._id || order?.orderId || id;
      const reason = cancellationReason.trim();
      const response = await AuthInterceptor(`/admin/orders/${targetOrderId}/status`, {
        method: "PUT",
        body: {
          orderStatus: "Cancelled",
          cancellationReason: reason.trim(),
          message: reason.trim(),
        },
      });
      if (response?.data?.s === 200) {
        setOrder((current) => ({ ...current, orderStatus: "Cancelled", cancellationReason: reason.trim() }));
        setSnack({ notification: { message: response.data.m || "Order cancelled", type: "success" } });
        setShowCancelDialog(false);
        setCancellationReason("");
      } else {
        throw new Error(response?.data?.m || "Unable to cancel order");
      }
    } catch (error) {
      setSnack({ notification: { message: error?.message || "Unable to cancel order", type: "error" } });
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <Spinnerr show={true} />;

  if (!order) {
    return (
      <div className="card p-4 text-center">
        <p className="text-muted mb-3">Order not found</p>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>
    );
  }

  const members = Array.isArray(order.members) ? order.members : [];
  const reports = Array.isArray(order.reports) ? order.reports : [];
  const assignedAgent = order.assignedAgent;
  const allItems = members.flatMap((member) =>
    (member.items || []).map((item) => ({
      ...item,
      memberName: member.fullName,
    })),
  );

  return (
    <>
      {snack && (
        <Toaster
          notification={snack.notification}
          onClose={() => setSnack(null)}
        />
      )}

      {showCancelDialog && (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0, 0, 0, 0.45)" }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Cancel Order</h5>
                <button type="button" className="btn-close" onClick={() => setShowCancelDialog(false)} disabled={cancelling} />
              </div>
              <div className="modal-body">
                <p className="mb-2">Are you sure you want to cancel this order?</p>
                <label htmlFor="cancellationReason" className="form-label">Cancellation reason (optional)</label>
                <textarea
                  id="cancellationReason"
                  className="form-control"
                  rows="3"
                  value={cancellationReason}
                  onChange={(event) => setCancellationReason(event.target.value)}
                  placeholder="Enter cancellation reason"
                  disabled={cancelling}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCancelDialog(false)} disabled={cancelling}>
                  Keep Order
                </button>
                <button type="button" className="btn btn-danger" onClick={cancelOrder} disabled={cancelling}>
                  {cancelling ? "Cancelling..." : "Cancel Order"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card shadow-sm border-0 rounded-3 p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">Order Details</h4>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
          {String(order.orderStatus || "").toLowerCase() !== "cancelled" && (
            <button className="btn btn-outline-danger btn-sm ms-2" onClick={() => setShowCancelDialog(true)} disabled={cancelling}>
              {cancelling ? "Cancelling..." : "Cancel Order"}
            </button>
          )}
        </div>

        <Section title="Order Information">
          <Info label="Order ID" value={formatOrderId(order)} />
          <Info label="Order Number" value={order.orderNumber} />
          <Info label="Order Date" value={formatDateTime(order.createdAt)} />
          <Info label="Order Status" value={order.orderStatus || "Pending"} />
          {order.cancellationReason ? (
            <Info label="Cancellation Reason" value={order.cancellationReason} />
          ) : null}
          <Info label="Lab Status" value={getLabStatus(order)} />
          <Info label="Hard copy" value={getHardCopyLabel(order)} />
          <Info label="Last Update Date" value={formatDateTime(order.updatedAt)} />
        </Section>

        <Section title="Assigned Delivery Agent">
          {assignedAgent ? (
            <>
              <Info label="Technician Name" value={assignedAgent.fullName} />
              <Info label="Mobile" value={assignedAgent.mobileNumber} />
              <Info label="Gender" value={assignedAgent.gender} />
              <Info label="Age" value={assignedAgent.age} />
              <Info label="Alternative Mobile Number" value={assignedAgent.alterMobileNumber} />
              <Info label="Email Id" value={assignedAgent.email} />
              <Info
                label="Full Address"
                value={[
                  assignedAgent.address1,
                  assignedAgent.address2,
                  assignedAgent.cityName,
                  assignedAgent.pincode,
                ].filter(Boolean).join(", ")}
              />
              <Info label="Lab Name" value={assignedAgent.lab?.labName} />
              <Info
                label="Lab Registration Number"
                value={assignedAgent.lab?.licenceNumber}
              />
              <Info
                label="Lab Full Address"
                value={[
                  assignedAgent.lab?.address1,
                  assignedAgent.lab?.address2,
                  assignedAgent.lab?.landmark,
                  assignedAgent.lab?.pincode,
                ].filter(Boolean).join(", ")}
              />
            </>
          ) : (
            <div className="col-12 text-muted">No agent assigned</div>
          )}
        </Section>

        <Section title="Price Summary">
          <div className="col-md-6">
            <PriceSummary order={order} />
          </div>
        </Section>

        <Section title="Address Details">
          <Info
            label="Type"
            value={getAddressValue(order.address, ["type", "addressType"])}
          />
          <Info
            label="House No./Flat No."
            value={getAddressValue(order.address, [
              "houseNo",
              "flatNo",
              "houseNumber",
            ])}
          />
          <Info
            label="Apartment/Building/Colony"
            value={getAddressValue(order.address, [
              "apartment",
              "building",
              "colony",
              "addressLine1",
            ])}
          />
          <Info
            label="Landmark"
            value={getAddressValue(order.address, ["landmark", "landMark"])}
          />
          <Info
            label="Pincode"
            value={getAddressValue(order.address, ["pincode", "pinCode"])}
          />
          <Info label="Town" value={getAddressValue(order.address, ["town"])} />
          <Info label="City" value={getAddressValue(order.address, ["city", "cityName"])} />
          <Info
            label="Service Area"
            value={getAddressValue(order.address, ["serviceLocationName"])}
          />
          <Info
            label="Latitude"
            value={getAddressValue(order.address, ["latitude", "lat"])}
          />
          <Info
            label="Longitude"
            value={getAddressValue(order.address, ["longitude", "lng", "long"])}
          />
        </Section>

        <Section title="Slot Details">
          <Info label="Date" value={order.slot?.date || "-"} />
          <Info label="Slot Period" value={getSlotPeriod(order.slot)} />
        </Section>

        <Section title="Members Details">
          {members.length > 0 ? (
            members.map((member, index) => (
              <div key={member._id || index} className="col-12">
                <div className="border rounded-3 p-3">
                  <h6 className="mb-3">
                    Member {index + 1}: {member.fullName || "-"}
                    <HardCopyBadge member={member} />
                  </h6>
                  <div className="row g-3">
                    <Info label="Gender" value={member.gender} />
                    <Info label="Age" value={member.age} />
                    <Info label="Relation" value={member.relation} />
                    <Info label="Mobile" value={member.mobileNumber} />
                    <Info label="Email" value={member.email} />
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-12 text-muted">No members found</div>
          )}
        </Section>

        <Section title="Order Item">
          <div className="col-12">
            {allItems.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-bordered table-sm align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Member</th>
                      <th>Type</th>
                      <th>Title</th>
                      <th>Original Price</th>
                      <th>Discounted Price</th>
                      <th>Discount %</th>
                      <th>Parameters/Tests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allItems.map((item, index) => (
                      <tr key={item.orderItemId || item._id || index}>
                        <td>{item.memberName || "-"}</td>
                        <td>{formatItemType(item.type)}</td>
                        <td>{item.title || "-"}</td>
                        <td>{formatCurrency(item.originalPrice)}</td>
                        <td>{formatCurrency(item.discountedPrice)}</td>
                        <td>
                          {item.discountPercent !== undefined
                            ? `${item.discountPercent}%`
                            : "-"}
                        </td>
                        <td>{getItemParameters(item)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-muted">No order items found</div>
            )}
          </div>
        </Section>

        <Section title="Reports">
          <div className="col-12">
            <MemberReports members={members} reports={reports} notify={setSnack} />
          </div>
        </Section>
      </div>
    </>
  );
};

const Section = ({ title, children }) => (
  <>
    <h5 className="mt-4 mb-3">{title}</h5>
    <div className="row g-3">{children}</div>
    <hr />
  </>
);

const PriceSummary = ({ order }) => {
  const summary = order.priceSummary || {};
  const hardCopyNames = getHardCopyMemberNames(order);
  const rows = [
    { label: "MRP Total", value: formatAmount(summary.originalTotal) },
    { label: "Catalogue Discount", value: `- ${formatAmount(summary.catalogueDiscount)}` },
    { label: "Tests Total", value: formatAmount(summary.testsTotal) },
    {
      label: summary.hardCopy ? `Hard Copy (${hardCopyNames.join(", ")})` : "Hard Copy",
      value: summary.hardCopy ? formatAmount(summary.hardCopyFee) : "Not opted",
    },
    { label: "Subtotal", value: formatAmount(summary.subtotal), strong: true },
    {
      label: summary.couponCode ? `Coupon (${summary.couponCode})` : "Coupon",
      value: `- ${formatAmount(summary.couponDiscount)}`,
    },
  ];

  return (
    <table className="table table-sm mb-0">
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <td className={row.strong ? "fw-semibold" : "text-muted"}>{row.label}</td>
            <td className={`text-end ${row.strong ? "fw-semibold" : ""}`}>{row.value}</td>
          </tr>
        ))}
        <tr className="table-light">
          <td className="fw-bold">Final Amount</td>
          <td className="text-end fw-bold">{formatAmount(summary.finalAmount)}</td>
        </tr>
        <tr>
          <td className="text-muted small" colSpan="2">
            GST Included: {formatAmount(summary.gstIncluded)} (CGST {formatAmount(summary.cgst)} + SGST{" "}
            {formatAmount(summary.sgst)})
          </td>
        </tr>
      </tbody>
    </table>
  );
};

const MemberReports = ({ members, reports, notify }) => {
  if (members.length === 0) return <div className="text-muted">No members found</div>;

  const findReport = (reportId) =>
    reports.find((report) => String(report._id) === String(reportId)) || { _id: reportId };

  return (
    <div className="row g-3">
      {members.map((member, index) => (
        <div key={member.orderMemberId || index} className="col-md-6">
          <div className="border rounded-3 p-3 h-100">
            <h6 className="mb-3">
              {member.fullName || "-"}
              {member.relation ? <span className="text-muted fw-normal"> ({member.relation})</span> : null}
              <HardCopyBadge member={member} />
            </h6>
            {(member.items || []).length > 0 ? (
              <ul className="list-group list-group-flush">
                {member.items.map((item, itemIndex) => {
                  const report = item.reportId ? findReport(item.reportId) : null;
                  return (
                    <li
                      key={item.orderItemId || itemIndex}
                      className="list-group-item px-0 d-flex justify-content-between align-items-center gap-2"
                    >
                      <div className="d-flex flex-wrap align-items-center gap-2 text-break">
                        <span>{item.title || "-"}</span>
                        <span className={`badge ${isPackageItem(item) ? "bg-info text-dark" : "bg-secondary"}`}>
                          {isPackageItem(item) ? "Package" : "Test"}
                        </span>
                      </div>
                      {report ? (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 flex-shrink-0"
                          title={report.originalFileName || "Report"}
                          onClick={() => openReport(report, notify)}
                        >
                          View
                        </button>
                      ) : (
                        <span className="badge bg-warning text-dark flex-shrink-0">Pending</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="text-muted small">No tests or packages</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const Info = ({ label, value }) => (
  <div className="col-md-4">
    <div className="text-muted small">{label}</div>
    <div className="fw-semibold">{value || "-"}</div>
  </div>
);

export default ViewOrder;
